'use client'
import { useQuery } from '@tanstack/react-query'
import { Globe, Send, StickyNote } from 'lucide-react'
import Markdown from 'markdown-to-jsx'
import Link from 'next/link'
import { type PropsWithChildren, useMemo } from 'react'
import { useSelectedTickerInfo } from '@/src/hooks/token-info/use-selected-ticker'
import { assetDataQuery } from '@/src/queries/keys/token-info/asset-data'
import { useTokenFieldActions } from '@/src/stores/swap/actions'
import { Button } from '../ui/button'
import { Github } from '../ui/icons/github'
import { SocialX } from '../ui/icons/social-x'
import { Skeleton } from '../ui/skeleton'
import ContractAddressDropdown from './about/contract-address-dropdown'

export default function About() {
  const {
    chain: chain_id,
    chains,
    denom,
    activeToken,
    isLoading,
    primaryToken,
  } = useSelectedTickerInfo()
  const { setValue } = useTokenFieldActions({ direction: activeToken })

  const { data, isLoading: isAssetDataLoading } = useQuery({
    ...assetDataQuery({ chainId: chain_id, denom }),
    enabled: !!chain_id && !!denom,
    staleTime: 2 * 60 * 1000,
  })

  const aggregatedData = useMemo(() => {
    if (!data) return

    return [
      {
        title: 'Website',
        icon: Globe,
        href: data.links.blockchain_site[0],
      },
      {
        title: `X · @${data.links.twitter_screen_name}`,
        icon: SocialX,
        href: `https://x.com/${data.links.twitter_screen_name}`,
      },
      {
        title: 'Github',
        icon: Github,
        href: data.links.repos_url.github[0],
      },
      {
        title: 'Telegram',
        icon: Send,
        href: data.links.telegram_channel_identifier
          ? `https://t.me/${data.links.telegram_channel_identifier}`
          : undefined,
      },
      {
        title: 'Whitepaper',
        icon: StickyNote,
        href: data.links.whitepaper,
      },
    ]
  }, [data])

  return (
    <div className='flex flex-col py-6 gap-3'>
      <div className='flex flex-col flex-wrap gap-3 sm:flex-row sm:items-center sm:justify-between md:flex-col md:items-start lg:flex-row lg:items-center -mx-5 overflow-hidden'>
        <h6 className='text-primary font-semibold text-xl px-5'>
          About {primaryToken?.display_name}
        </h6>
        <div className='overflow-x-auto no-scrollbar sm:mr-0 w-full'>
          <div className='inline-flex flex-nowrap sm:flex-wrap items-center gap-2 px-5 py-2 relative'>
            {chains && chains.length > 0 && (
              <ContractAddressDropdown
                chains={chains}
                onClick={(data) => {
                  setValue({
                    ...data,
                  })
                }}
              />
            )}
            {!isAssetDataLoading
              ? aggregatedData?.map(({ href, title, icon }) => {
                  if (!href) return null

                  const Icon = icon

                  return (
                    <Link key={`badge-${title}`} href={href} target='_blank'>
                      <Button
                        size='sm'
                        squared
                        variant='secondary'
                        type='button'
                        title={title}
                      >
                        <Icon width={16} height={16} className='size-4' />
                      </Button>
                    </Link>
                  )
                })
              : Array(4)
                  .fill(null)
                  .map((_, i) => (
                    <Skeleton
                      key={`badge-loader-${i}`}
                      className='w-16 h-8 rounded-[32px]'
                    />
                  ))}
          </div>
        </div>
      </div>
      {!isLoading && !isAssetDataLoading ? (
        data?.description?.en ? (
          <Markdown
            className='max-w-[576px] flex flex-col gap-4 text-secondary'
            options={{
              overrides: {
                a: {
                  component: MDLink,
                },
                p: {
                  component: MDParagraph,
                },
              },
            }}
          >
            {data?.description.en}
          </Markdown>
        ) : (
          <p className='text-secondary leading-7'>
            No information available about this token.
          </p>
        )
      ) : (
        <div className='flex flex-col gap-2 py-1.5 w-full max-w-[576px]'>
          <Skeleton className='w-full h-4 rounded-[8px]' delay='100ms' />
          <Skeleton className='w-[512px] h-4 rounded-[8px]' delay='200ms' />
          <Skeleton className='w-full h-4 rounded-[8px]' delay='300ms' />
          <Skeleton className='w-72 h-4 rounded-[8px]' delay='400ms' />
        </div>
      )}
    </div>
  )
}

const MDLink = ({
  children,
  ...props
}: PropsWithChildren<{ href: string }>) => (
  <Link {...props} href={props.href ?? '/'} className='text-accent-primary'>
    {children}
  </Link>
)

const MDParagraph = ({ children, ...props }: PropsWithChildren<unknown>) => (
  <p
    {...props}
    className='text-secondary whitespace-break-spaces leading-7 text-base'
  >
    {children}
  </p>
)
